import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  acquireModalLayer,
  releaseModalLayer,
  isTopModalLayer,
  isAnyLayerAbove,
  modalZIndex,
} from '../../lib/modalStack';

/**
 * Modal — production-grade portal modal
 *
 * Features
 * ─────────
 * • Rendered into document.body via React portal (never clipped by overflow)
 * • position:fixed backdrop — always viewport-centred regardless of page scroll
 * • Body scroll lock with scrollbar-width compensation (no layout shift)
 * • Reference-counted scroll lock for nested modals
 * • Global stacking: every popup registers with the modal stack manager, so a
 *   popup opened from inside another popup always renders ABOVE the existing
 *   one (unlimited nesting). Custom overlays register the same way via
 *   useModalLayer(). The z-index prop is deprecated/ignored.
 * • Escape key closes ONLY the topmost modal
 * • Click-outside (backdrop) to close when onClose is provided
 * • Focus trap (Tab / Shift+Tab cycles within panel)
 * • Focus restored to previous element on unmount
 * • ARIA: role="dialog", aria-modal="true", aria-labelledby / aria-label support
 * • size prop: named panel widths (sm/md/lg/xl/full) applied to the panel
 *   wrapper; title prop supplies the accessible name when neither labelId
 *   nor ariaLabel is given (it is never rendered as a header)
 * • GPU-composited layer for smooth open/close
 * • Respects prefers-reduced-motion
 * • SSR-safe (guards document access)
 * • fullScreen prop: inner wrapper fills entire overlay (for immersive mobile UIs)
 *
 * Usage
 * ───────
 * <Modal onClose={() => setOpen(false)} labelId="my-modal-title">
 *   <div className="bg-white rounded-2xl p-6">
 *     <h2 id="my-modal-title">…</h2>
 *   </div>
 * </Modal>
 *
 * // Full-screen mobile overlay (no backdrop padding, panel fills viewport):
 * <Modal fullScreen onClose={...}>
 *   <div className="flex flex-col h-full">…</div>
 * </Modal>
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Named size → panel wrapper max-width class. The scale follows the widths
 * Modal children actually hard-code today (tree-wide census: max-w-md ×140,
 * max-w-lg ×94, max-w-sm ×86, max-w-xl ×22), in ascending order.
 */
const MODAL_SIZE_MAX_WIDTH: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-full',
};

interface ModalProps {
  onClose?: () => void;
  /** Extra Tailwind classes applied to the backdrop div (rarely needed). */
  backdropClassName?: string;
  children: React.ReactNode;
  /**
   * Optional modal title. Never rendered; used as the dialog's accessible
   * name (aria-label) when neither labelId nor ariaLabel is provided.
   */
  title?: string;
  /**
   * Optional named panel width (see MODAL_SIZE_MAX_WIDTH). When set, the
   * size class replaces the panel wrapper's default max width, so the
   * Modal's width wins over any width a child sets on itself. When omitted,
   * the wrapper keeps max-w-[calc(100dvw-2rem)] and children size
   * themselves, exactly as before. Ignored when fullScreen is set.
   */
  size?: ModalSize;
  /**
   * @deprecated z-index is assigned automatically by the global modal stack —
   * every popup opened later always paints above every currently open popup.
   */
  zIndex?: number;
  /** Set false to disable body scroll lock (default: true). */
  scrollLock?: boolean;
  /** id of the element that labels this dialog — forwarded to aria-labelledby. */
  labelId?: string;
  /** Accessible name for the dialog when no labelId is provided. */
  ariaLabel?: string;
  /**
   * When true, the inner wrapper fills the entire fixed overlay (w-full h-full)
   * instead of centring with my-auto. Use for full-screen mobile chat / detail views.
   * The backdrop gets `p-0 items-stretch` automatically; you do NOT need to
   * override those classes via backdropClassName.
   * Default: false.
   */
  fullScreen?: boolean;
}

function getScrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth;
}

// ── Reference-counted scroll lock for nested modals ──
let scrollLockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function acquireScrollLock() {
  if (scrollLockCount === 0) {
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    const sbw = getScrollbarWidth();
    document.body.style.setProperty('--scrollbar-width', `${Math.max(sbw, 0)}px`);
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = sbw > 0 ? `${sbw}px` : '';
    document.body.dataset.scrollLocked = 'true';
  }
  scrollLockCount++;
}

function releaseScrollLock() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
    document.body.style.removeProperty('--scrollbar-width');
    delete document.body.dataset.scrollLocked;
  }
}

export default function Modal({
  onClose,
  backdropClassName = '',
  children,
  title,
  size,
  zIndex: _deprecatedZIndex,
  scrollLock = true,
  labelId,
  ariaLabel,
  fullScreen = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const layerIdRef = useRef<number | null>(null);

  if (layerIdRef.current === null) {
    layerIdRef.current = acquireModalLayer();
  }
  const layerZ = modalZIndex(layerIdRef.current);

  useEffect(() => { setMounted(true); }, []);

  // ── Close transition (Section 5.1): reverse the open animation at
  // duration-fast / ease-accelerate before unmounting. ──
  const closingRef = useRef(false);
  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    if (!onClose) return;
    closingRef.current = true;
    setClosing(true);
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      onClose();
    };
    // Allow the exit animation to play; fall back if onAnimationEnd is missed.
    const t = setTimeout(finish, 160);
    const onAnimEnd = (e: AnimationEvent) => {
      if (e.target === panelRef.current || e.target === panelRef.current?.firstElementChild) {
        clearTimeout(t);
        panelRef.current?.removeEventListener('animationend', onAnimEnd);
        finish();
      }
    };
    panelRef.current?.addEventListener('animationend', onAnimEnd);
  }, [onClose]);

  // Register with the global modal stack so this popup paints above every
  // popup that was open before it, and below every popup opened after it.
  useLayoutEffect(() => {
    if (layerIdRef.current === null) {
      layerIdRef.current = acquireModalLayer();
    }
    const id = layerIdRef.current;
    return () => {
      releaseModalLayer(id);
      layerIdRef.current = null;
    };
  }, []);

  // ── Escape key — only the TOPMOST modal responds, so pressing Escape with
  // nested popups closes just the newest one; focus then returns to the popup
  // underneath via the focus-trap cleanup below. ──
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (layerIdRef.current != null && !isTopModalLayer(layerIdRef.current)) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, requestClose]);

  // ── Body scroll lock with reference counting ──
  useEffect(() => {
    if (!scrollLock) return;
    acquireScrollLock();
    return () => releaseScrollLock();
  }, [scrollLock]);

  // ── Focus trap — runs once the panel is actually rendered (mounted=true). ──
  useEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]';

    const prevFocus = document.activeElement as HTMLElement | null;

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const first = panel.querySelector<HTMLElement>(focusableSelector);
        if (first) { first.focus(); }
        else { panel.setAttribute('tabindex', '-1'); panel.focus(); }
      });
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const nodes = panel.querySelectorAll<HTMLElement>(focusableSelector);
      if (nodes.length === 0) { e.preventDefault(); panel.focus(); return; }
      const first = nodes[0];
      const last  = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === panel) {
          e.preventDefault(); last.focus();
        }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown);
      // Hand focus back to whatever was focused before this popup opened —
      // when nested, that element lives inside the popup below, so closing
      // the top popup returns focus to the previous popup. Never steal focus
      // from a popup that is still open ABOVE us. Our layer was already
      // released by this point, so check for any still-open higher layer.
      if (layerIdRef.current == null || !isAnyLayerAbove(layerIdRef.current)) {
        prevFocus?.focus();
      }
    };
  }, [mounted]);

  if (!mounted) return null;

  // When fullScreen=true: no padding on backdrop, inner wrapper fills entire overlay.
  // When fullScreen=false: standard centred modal with p-4 and my-auto.
  const backdropBase = fullScreen
    ? `modal-backdrop ${closing ? 'portal-backdrop-out' : 'portal-backdrop-in'} fixed inset-0 flex items-stretch`
    : `modal-backdrop ${closing ? 'portal-backdrop-out' : 'portal-backdrop-in'} fixed inset-0 bg-black/45 flex items-center justify-center p-4 overflow-y-auto overscroll-contain`;

  const innerClass = fullScreen
    ? 'w-full h-full outline-none flex items-center justify-center overflow-x-hidden'
    : `${closing ? 'portal-modal-out' : 'portal-modal-in'} my-auto outline-none w-full ${size ? MODAL_SIZE_MAX_WIDTH[size] : 'max-w-[calc(100dvw-2rem)]'} overflow-x-hidden flex items-center justify-center`;

  return createPortal(
    <div
      className={[backdropBase, backdropClassName].join(' ')}
      style={{ zIndex: layerZ }}
      onMouseDown={onClose
        ? (e) => { if (e.target === e.currentTarget) requestClose(); }
        : undefined
      }
      role="dialog"
      aria-modal="true"
      {...(labelId
        ? { 'aria-labelledby': labelId }
        : ariaLabel
          ? { 'aria-label': ariaLabel }
          : title
            ? { 'aria-label': title }
            : {})}
    >
      <div
        ref={panelRef}
        className={innerClass}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
